// Pagos y acreditaciones bancarias del rol cerrado. Nómina → Rol de Pagos.
// Tres cosas: quién puede cobrar por transferencia y quién no, el archivo que
// se sube al banco (con el layout de cada banco como dato editable) y el
// desembolso, que registra UN movimiento en Control Bancario por el total.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Banknote, Download, Loader2, Plus, Settings2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { cb } from '@/features/control-bancario/shared';
import {
  MESES, TIPOS_ROL, Campo, Modal, fieldCls, money, nomRpc, hoyISO, useDialogoEnLinea,
  CAMPOS_ARCHIVO, type Acreditacion, type ArchivoBanco, type CampoFormato,
  type PagoFormato, type PagoValidacion, type RolHeader,
} from './shared';
import { EstadoRol } from './Rol';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

interface CuentaMini { id: string; alias: string | null; numero: string; moneda: string; banco: { nombre: string } | null }

function descargarTexto(nombre: string, contenido: string) {
  const blob = new Blob(['﻿' + contenido], { type: 'text/plain;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function Pagos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: roles = [], isFetching } = useQuery<RolHeader[]>({
    queryKey: ['nom-roles'],
    queryFn: () => nomRpc<RolHeader[]>('nom_roles', {}),
    staleTime: 0,
  });
  const pagables = roles.filter((r) => r.estado !== 'borrador');
  const [selId, setSelId] = useState('');
  const rol = pagables.find((r) => r.id === selId) ?? null;

  const { data: acreditaciones = [], isFetching: cargandoAcr } = useQuery<Acreditacion[]>({
    queryKey: ['nom-rol-acred', selId],
    queryFn: () => nomRpc<Acreditacion[]>('nom_rol_acreditaciones', { _id: selId }),
    enabled: !!selId,
  });
  const { data: val } = useQuery<PagoValidacion>({
    queryKey: ['nom-rol-pago-validar', selId],
    queryFn: () => nomRpc<PagoValidacion>('nom_rol_pago_validar', { _rol: selId }),
    enabled: !!selId,
  });
  const { data: formatos = [] } = useQuery<PagoFormato[]>({
    queryKey: ['nom-pago-formatos', 'banco'],
    queryFn: () => nomRpc<PagoFormato[]>('nom_pago_formatos', { _uso: 'banco' }),
  });
  const { data: cuentas = [] } = useQuery<CuentaMini[]>({
    queryKey: ['cb-cuentas-nomina'],
    queryFn: () => cb.list<CuentaMini>('cuentas',
      '?select=id,alias,numero,moneda,banco:banco_id(nombre)&deleted_at=is.null&es_activo=is.true&order=alias,numero'),
  });

  const [formatoId, setFormatoId] = useState('');
  const [omitir, setOmitir] = useState(false);
  const [cuentaId, setCuentaId] = useState('');
  const [formatosAbierto, setFormatosAbierto] = useState(false);

  const bajar = useMutation({
    mutationFn: () => nomRpc<ArchivoBanco>('nom_rol_archivo_banco',
      { _rol: selId, _formato_id: formatoId || null, _omitir_incompletos: omitir }),
    onSuccess: (a) => {
      descargarTexto(a.archivo, a.contenido);
      toast.success(`${a.archivo}: ${a.lineas} línea(s) por ${money(a.total)}`);
      if (a.omitidos?.length) toast.warning(`${a.omitidos.length} persona(s) quedaron fuera del archivo: hay que pagarles por otra vía.`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pagar = useMutation({
    mutationFn: () => nomRpc('nom_rol_pagar', { _id: selId, _fecha: hoyISO(), _cuenta_id: cuentaId || null }),
    onSuccess: () => {
      toast.success(cuentaId ? 'Rol pagado y movimiento registrado en el banco' : 'Rol marcado como pagado');
      void qc.invalidateQueries({ queryKey: ['nom-roles'] });
      void qc.invalidateQueries({ queryKey: ['cb-movimientos'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const total = acreditaciones.reduce((s, a) => s + Number(a.neto), 0);

  const headersExp = ['N°', 'Código', 'Empleado', 'Banco', 'Tipo', 'Cuenta', 'Neto'];
  const MONEYCOLS = [6];
  const filasExport: Celda[][] = [
    ...acreditaciones.map((a, i): Celda[] => [
      i + 1, a.empleado_codigo ?? '', a.razon_social, a.banco_nombre ?? 'Sin banco',
      a.banco_tipo_cuenta ?? '—', a.banco_cuenta ?? '—', Number(a.neto),
    ]),
    ['', '', '', '', '', 'TOTAL', total],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold">Pagos y acreditaciones</h1>
        <Button variant="outline" size="sm" onClick={() => setFormatosAbierto(true)}>
          <Settings2 className="w-4 h-4 mr-1" /> Formatos del banco
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select className={`${fieldCls} w-72`} value={selId} onChange={(e) => { setSelId(e.target.value); setOmitir(false); }} aria-label="Rol">
          <option value="">— Selecciona un rol cerrado —</option>
          {pagables.map((r) => (
            <option key={r.id} value={r.id}>
              {MESES[r.mes - 1]} {r.anio}{r.tipo !== 'general' ? ` · ${TIPOS_ROL[r.tipo]}` : ''} ({r.codigo}) — {r.estado} — {money(r.total_neto)}
            </option>
          ))}
        </select>
        {rol && rol.tipo !== 'general' && (
          <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">{TIPOS_ROL[rol.tipo]}</span>
        )}
        {rol && <EstadoRol estado={rol.estado} />}
        {(isFetching || cargandoAcr) && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
      </div>

      {rol && (
        <>
          {/* Quién puede cobrar por transferencia y quién no. Dejar a alguien
              fuera en silencio es la peor ayuda posible: el archivo saldría
              cuadrado y esa persona se entera cuando no le llega el sueldo. */}
          {val && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Listos para transferir</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-xs text-muted-foreground">Pagables</div><div className="text-lg font-semibold tabular-nums">{val.total}</div></div>
                  <div><div className="text-xs text-muted-foreground">Con cuenta</div><div className="text-lg font-semibold tabular-nums text-ok">{val.listos}</div></div>
                  <div><div className="text-xs text-muted-foreground">Sin datos bancarios</div><div className={`text-lg font-semibold tabular-nums ${val.incompletos > 0 ? 'text-pendiente' : ''}`}>{val.incompletos}</div></div>
                  <div><div className="text-xs text-muted-foreground">Monto trabado</div><div className="text-lg font-semibold tabular-nums">{money(val.monto_trabado)}</div></div>
                </div>

                {val.incompletos > 0 && (
                  <div className="rounded-md border border-pendiente/40 bg-pendiente/5 p-3 space-y-2">
                    <div className="flex items-start gap-2 text-sm">
                      <AlertTriangle className="w-4 h-4 mt-0.5 text-pendiente shrink-0" />
                      <span>
                        A {val.incompletos} persona(s) no se les puede transferir. Completa su ficha en <b>Empleados</b>, o
                        marca la casilla para generar el archivo sin ellas — y entonces habrá que pagarles por otra vía.
                      </span>
                    </div>
                    <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                      {val.detalle.map((d, i) => (
                        <div key={i} className="flex justify-between gap-3 border-b border-border/40 pb-1 last:border-0">
                          <span>{d.empleado} <span className="font-mono text-muted-foreground">{d.codigo}</span></span>
                          <span className="text-pendiente">{d.falta}</span>
                          <span className="font-mono tabular-nums">{money(d.neto)}</span>
                        </div>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={omitir} onChange={(e) => setOmitir(e.target.checked)} />
                      Generar el archivo dejando fuera a esas {val.incompletos} persona(s)
                    </label>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <Campo label="Formato del banco" className="w-64">
              <select className={fieldCls} value={formatoId} onChange={(e) => setFormatoId(e.target.value)}>
                {formatos.filter((f) => f.activo).map((f) => (
                  <option key={f.id} value={f.id}>{f.nombre}{f.banco ? ` · ${f.banco}` : ''}</option>
                ))}
              </select>
            </Campo>
            <Button variant="outline" isLoading={bajar.isPending}
              disabled={!val || val.listos === 0 || (val.incompletos > 0 && !omitir)}
              onClick={() => bajar.mutate()}>
              <Download className="w-4 h-4 mr-1" /> Descargar archivo del banco
            </Button>
          </div>

          {rol.estado === 'cerrado' && (
            <div className="flex flex-wrap items-end gap-3">
              <Campo label="Cuenta desde la que sale el dinero" className="w-72">
                <select className={fieldCls} value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
                  <option value="">— Sin registrar en el banco —</option>
                  {cuentas.map((c) => (
                    <option key={c.id} value={c.id}>{c.alias ?? c.banco?.nombre ?? 'Cuenta'} · {c.numero}</option>
                  ))}
                </select>
              </Campo>
              <Button isLoading={pagar.isPending} onClick={async () => {
                const aviso = cuentaId
                  ? `Se marcará como pagado y se registrará un movimiento de ${money(rol.total_neto)} en la cuenta seleccionada.`
                  : 'Se marcará como pagado SIN registrar el movimiento bancario (el desembolso quedará fuera de Control Bancario).';
                if (await dlg.confirmar(`¿Continuar con el rol ${rol.codigo}?`, { detalle: aviso })) pagar.mutate();
              }}>
                <Banknote className="w-4 h-4 mr-1" /> Marcar como pagado
              </Button>
            </div>
          )}
          {rol.estado === 'pagado' && rol.fecha_pagado && (
            <div className="text-sm text-muted-foreground">Pagado el {rol.fecha_pagado}</div>
          )}

          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Acreditaciones ({acreditaciones.length}) — total {money(total)}</CardTitle>
              <ExportarBotones
                nombre={`acreditaciones-${fechaLocal()}`} titulo="Pagos y acreditaciones"
                headers={headersExp} filas={filasExport} colsDerecha={[0, ...MONEYCOLS]}
                fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
                disabled={acreditaciones.length === 0} />
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Banco</th>
                  <th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3 text-right">Neto</th>
                </tr></thead>
                <tbody>
                  {acreditaciones.map((a, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 pr-3 font-mono">{a.empleado_codigo}</td>
                      <td className="py-2 pr-3">{a.razon_social}<div className="text-xs text-muted-foreground font-mono">{a.identificacion}</div></td>
                      <td className="py-2 pr-3">{a.banco_nombre ?? <span className="text-pendiente">Sin banco</span>}</td>
                      <td className="py-2 pr-3">{a.banco_tipo_cuenta ?? '—'}</td>
                      <td className="py-2 pr-3 font-mono">{a.banco_cuenta ?? '—'}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(a.neto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {!rol && !isFetching && (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          Cierra un rol en «Generar rol» y aparecerá aquí para gestionar su pago.
        </CardContent></Card>
      )}

      {formatosAbierto && <FormatosModal formatos={formatos} onClose={() => setFormatosAbierto(false)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// El layout de cada banco, editable aquí mismo. No está escrito en el código a
// propósito: los bancos cambian anchos sin avisar y el rechazo llega el día del
// pago, cuando esperar un despliegue no es una opción.
function FormatosModal({ formatos, onClose }: { formatos: PagoFormato[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [sel, setSel] = useState<PagoFormato | null>(formatos[0] ?? null);
  const [form, setForm] = useState(() => aForm(formatos[0]));

  function aForm(f: PagoFormato | undefined | null) {
    return {
      id: f?.id ?? '', nombre: f?.nombre ?? '', banco: f?.banco ?? '',
      tipo: f?.tipo ?? 'csv', separador: f?.separador ?? ',',
      encabezado: f?.encabezado ?? true, extension: f?.extension ?? 'csv',
      fin_linea: f?.fin_linea ?? 'lf', activo: f?.activo ?? true,
      campos: (f?.campos ?? []) as CampoFormato[],
    };
  }
  const elegir = (f: PagoFormato | null) => { setSel(f); setForm(aForm(f)); };

  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_pago_formato_guardar', {
      _data: {
        id: form.id || null, nombre: form.nombre, banco: form.banco || null,
        tipo: form.tipo, separador: form.separador, encabezado: form.encabezado,
        extension: form.extension, fin_linea: form.fin_linea, activo: form.activo,
        campos: form.campos,
      },
    }),
    onSuccess: () => { toast.success('Formato guardado'); void qc.invalidateQueries({ queryKey: ['nom-pago-formatos'] }); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const setCampo = (i: number, patch: Partial<CampoFormato>) =>
    setForm({ ...form, campos: form.campos.map((c, j) => (j === i ? { ...c, ...patch } : c)) });

  return (
    <Modal title="Formatos del archivo del banco" onClose={onClose} wide>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {formatos.map((f) => (
            <button key={f.id} type="button"
              className={`px-3 py-1 rounded-full text-xs border ${sel?.id === f.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}
              onClick={() => elegir(f)}>
              {f.nombre}{f.es_sistema ? ' ·' : ''}
            </button>
          ))}
          <button type="button" className="px-3 py-1 rounded-full text-xs border border-dashed"
            onClick={() => elegir(null)}>
            <Plus className="w-3 h-3 inline -mt-0.5 mr-1" />Nuevo
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Campo label="Nombre"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
          <Campo label="Banco"><input className={fieldCls} value={form.banco} onChange={(e) => setForm({ ...form, banco: e.target.value })} /></Campo>
          <Campo label="Tipo">
            <select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as 'csv' | 'fijo' })}>
              <option value="csv">Separado (CSV/TXT)</option>
              <option value="fijo">Ancho fijo</option>
            </select>
          </Campo>
          <Campo label="Extensión"><input className={fieldCls} value={form.extension} onChange={(e) => setForm({ ...form, extension: e.target.value })} /></Campo>
          {form.tipo === 'csv' && (
            <>
              <Campo label="Separador"><input className={fieldCls} value={form.separador} onChange={(e) => setForm({ ...form, separador: e.target.value })} /></Campo>
              <Campo label="Encabezado">
                <select className={fieldCls} value={form.encabezado ? '1' : '0'} onChange={(e) => setForm({ ...form, encabezado: e.target.value === '1' })}>
                  <option value="1">Con títulos</option><option value="0">Sin títulos</option>
                </select>
              </Campo>
            </>
          )}
          <Campo label="Fin de línea">
            <select className={fieldCls} value={form.fin_linea} onChange={(e) => setForm({ ...form, fin_linea: e.target.value as 'lf' | 'crlf' })}>
              <option value="lf">LF (Unix)</option><option value="crlf">CRLF (Windows)</option>
            </select>
          </Campo>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Campos, en el orden en que los pide el banco</span>
            <Button size="sm" variant="outline" onClick={() => setForm({ ...form, campos: [...form.campos, { campo: 'identificacion', titulo: 'Identificacion' }] })}>
              <Plus className="w-4 h-4 mr-1" /> Añadir campo
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">#</th><th className="py-1 pr-2">Campo</th><th className="py-1 pr-2">Título</th>
                <th className="py-1 pr-2">Ancho</th><th className="py-1 pr-2">Relleno</th><th className="py-1 pr-2">Alineación</th><th />
              </tr></thead>
              <tbody>
                {form.campos.map((c, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-1 pr-2 tabular-nums text-muted-foreground">{i + 1}</td>
                    <td className="py-1 pr-2">
                      <select className={`${fieldCls} h-8`} value={c.campo ?? ''} onChange={(e) => setCampo(i, { campo: e.target.value || null })}>
                        {CAMPOS_ARCHIVO.map((o) => <option key={o.valor} value={o.valor}>{o.label}</option>)}
                      </select>
                    </td>
                    <td className="py-1 pr-2">
                      <input className={`${fieldCls} h-8`} value={c.campo ? (c.titulo ?? '') : (c.valor ?? '')}
                        placeholder={c.campo ? 'Título de la columna' : 'Valor constante'}
                        onChange={(e) => setCampo(i, c.campo ? { titulo: e.target.value } : { valor: e.target.value })} />
                    </td>
                    <td className="py-1 pr-2">
                      <input type="number" min={0} className={`${fieldCls} h-8 w-20`} value={c.ancho ?? ''}
                        onChange={(e) => setCampo(i, { ancho: e.target.value === '' ? null : Number(e.target.value) })} />
                    </td>
                    <td className="py-1 pr-2">
                      <input className={`${fieldCls} h-8 w-14`} maxLength={1} value={c.relleno ?? ''} placeholder="␣"
                        onChange={(e) => setCampo(i, { relleno: e.target.value })} />
                    </td>
                    <td className="py-1 pr-2">
                      <select className={`${fieldCls} h-8`} value={c.alinea ?? 'izq'} onChange={(e) => setCampo(i, { alinea: e.target.value as 'izq' | 'der' })}>
                        <option value="izq">Izquierda</option><option value="der">Derecha</option>
                      </select>
                    </td>
                    <td className="py-1">
                      <button type="button" className="text-destructive" aria-label="Quitar campo"
                        onClick={() => setForm({ ...form, campos: form.campos.filter((_, j) => j !== i) })}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            El ancho y el relleno sólo hacen falta en los archivos de ancho fijo. Si el banco te devuelve el
            archivo, corrige aquí lo que te señale: no hace falta esperar a nadie.
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!form.nombre || form.campos.length === 0}
            onClick={() => guardar.mutate()}>Guardar</Button>
        </div>
      </div>
    </Modal>
  );
}
